import { CreditLedger, loadOfAssignment, rankCandidates } from '@/domain/credit';
import { compareDates, dayTypeOf, meetingDatesBetween } from '@/domain/dates';
import {
  type Assignment,
  assignmentKey,
  type DayType,
  type IsoDate,
  type Rider,
  type Settings,
  type Volunteer,
} from '@/shared/types';

/**
 * Schedule generation (spec 4).
 *
 * Generation is explicit and scoped. It never reacts to an edit, never touches
 * a row outside the requested range, and never overwrites a confirmed row.
 * It returns a *preview* — a list of proposed changes — which the coordinator
 * approves before anything is written. The tool suggests; the human decides.
 */

export type SkipReason = 'confirmed' | 'manual' | 'settled';

export interface ProposedChange {
  date: IsoDate;
  riderId: string;
  dayType: DayType;
  /** Volunteer currently on the row, if any. */
  fromVolunteerId: string | null;
  /** Volunteer being proposed. Null when nobody is eligible. */
  toVolunteerId: string | null;
  /** True when the assignment row does not exist in the Sheet yet. */
  createsRow: boolean;
}

export interface SkippedRow {
  date: IsoDate;
  riderId: string;
  volunteerId: string | null;
  reason: SkipReason;
}

export interface UnfilledRow {
  date: IsoDate;
  riderId: string;
  dayType: DayType;
}

export interface GenerationPreview {
  start: IsoDate;
  end: IsoDate;
  changes: ProposedChange[];
  skipped: SkippedRow[];
  unfilled: UnfilledRow[];
  /** Meeting dates considered, for the summary line. */
  datesConsidered: number;
}

export interface GenerateOptions {
  start: IsoDate;
  end: IsoDate;
  /** Opt-in override for rows the coordinator has hand-edited (spec 4.2). */
  includeManual?: boolean;
  /** Defaults to every active rider. */
  riderIds?: string[];
}

function blankAssignment(date: IsoDate, riderId: string): Assignment {
  return {
    date,
    riderId,
    volunteerId: null,
    confirmed: false,
    outcome: 'Pending',
    legs: 'Both',
    manual: false,
    publicEventId: null,
    inviteEventId: null,
    notes: '',
  };
}

/**
 * Compute the changes a generate run would make. Pure: nothing is written and
 * no input is mutated.
 */
export function planGeneration(
  volunteers: readonly Volunteer[],
  riders: readonly Rider[],
  assignments: readonly Assignment[],
  settings: Settings,
  options: GenerateOptions,
): GenerationPreview {
  const { start, end, includeManual = false } = options;

  const targetRiders = (
    options.riderIds
      ? riders.filter((r) => options.riderIds!.includes(r.id))
      : riders.filter((r) => r.active)
  ).slice();

  const preview: GenerationPreview = {
    start,
    end,
    changes: [],
    skipped: [],
    unfilled: [],
    datesConsidered: 0,
  };

  if (targetRiders.length === 0 || compareDates(start, end) > 0) return preview;

  const existing = new Map(assignments.map((a) => [assignmentKey(a.date, a.riderId), a]));

  // Seed the ledger from *every* assignment, including rows outside the range.
  // Load already committed on either side of the window still has to count, or
  // a partial regenerate would double-book whoever is busy just outside it.
  const ledger = CreditLedger.from(volunteers, assignments, settings.rollingWindowWeeks);

  const dates = meetingDatesBetween(start, end, settings.meetingDays);
  preview.datesConsidered = dates.length * targetRiders.length;

  for (const date of dates) {
    const dayType = dayTypeOf(date, settings.meetingDays);
    if (!dayType) continue;

    for (const rider of targetRiders) {
      const key = assignmentKey(date, rider.id);
      const row = existing.get(key);

      if (row) {
        // A confirmed row is a promise already made to a volunteer. Changing
        // it is always a hand edit, never something a batch run does.
        if (row.confirmed) {
          preview.skipped.push({
            date,
            riderId: rider.id,
            volunteerId: row.volunteerId,
            reason: 'confirmed',
          });
          continue;
        }
        if (row.manual && !includeManual) {
          preview.skipped.push({
            date,
            riderId: rider.id,
            volunteerId: row.volunteerId,
            reason: 'manual',
          });
          continue;
        }
        if (row.outcome !== 'Pending') {
          preview.skipped.push({
            date,
            riderId: rider.id,
            volunteerId: row.volunteerId,
            reason: 'settled',
          });
          continue;
        }
      }

      const current = row ?? blankAssignment(date, rider.id);

      // Take the incumbent back off the books before ranking, so that a
      // regenerate re-competes the slot rather than penalising whoever
      // happened to hold it.
      if (current.volunteerId) {
        ledger.unrecord(current.volunteerId, date);
      }

      const ranked = rankCandidates(volunteers, ledger, date, dayType);
      const pick = ranked[0]?.volunteer ?? null;

      if (pick) {
        ledger.record(pick.id, date, loadOfAssignment({ ...current, volunteerId: pick.id }));
      } else {
        preview.unfilled.push({ date, riderId: rider.id, dayType });
      }

      const changed = (pick?.id ?? null) !== current.volunteerId;
      if (changed || !row) {
        preview.changes.push({
          date,
          riderId: rider.id,
          dayType,
          fromVolunteerId: current.volunteerId,
          toVolunteerId: pick?.id ?? null,
          createsRow: !row,
        });
      }
    }
  }

  return preview;
}

/**
 * Fold a preview into the assignment list, producing the rows to write.
 * Generated rows are explicitly `manual: false` so a later regenerate can
 * revisit them.
 */
export function applyPreview(
  assignments: readonly Assignment[],
  preview: GenerationPreview,
): { updated: Assignment[]; created: Assignment[] } {
  const byKey = new Map(assignments.map((a) => [assignmentKey(a.date, a.riderId), a]));
  const updated: Assignment[] = [];
  const created: Assignment[] = [];

  for (const change of preview.changes) {
    const key = assignmentKey(change.date, change.riderId);
    const row = byKey.get(key);

    if (row) {
      updated.push({ ...row, volunteerId: change.toVolunteerId, manual: false });
    } else {
      created.push({
        ...blankAssignment(change.date, change.riderId),
        volunteerId: change.toVolunteerId,
      });
    }
  }

  return { updated, created };
}

export interface NextUp {
  dayType: DayType;
  date: IsoDate | null;
  candidates: { volunteerId: string; name: string; credit: number; lastAssigned: IsoDate | null }[];
}

/**
 * The always-visible "next up" panel (spec 4.3): who the ranker would pick for
 * the next occurrence of each meeting day. This is what the coordinator reads
 * off when someone bails and a replacement is needed right now.
 */
export function nextUpPanels(
  volunteers: readonly Volunteer[],
  assignments: readonly Assignment[],
  settings: Settings,
  today: IsoDate,
  limit = 3,
): NextUp[] {
  const ledger = CreditLedger.from(volunteers, assignments, settings.rollingWindowWeeks);
  const horizon = meetingDatesBetween(
    today,
    // Eight weeks is plenty to find the next occurrence of each day type.
    meetingDatesLookahead(today),
    settings.meetingDays,
  );

  return settings.meetingDays.map((dayType) => {
    const date = horizon.find((d) => dayTypeOf(d, [dayType]) === dayType) ?? null;
    if (!date) return { dayType, date: null, candidates: [] };

    const ranked = rankCandidates(volunteers, ledger, date, dayType).slice(0, limit);
    return {
      dayType,
      date,
      candidates: ranked.map((c) => ({
        volunteerId: c.volunteer.id,
        name: c.volunteer.name,
        credit: c.credit,
        lastAssigned: c.lastAssigned,
      })),
    };
  });
}

function meetingDatesLookahead(today: IsoDate): IsoDate {
  const [y, m, d] = [Number(today.slice(0, 4)), Number(today.slice(5, 7)), Number(today.slice(8))];
  return new Date(Date.UTC(y, m - 1, d + 56)).toISOString().slice(0, 10);
}
